import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Users, User } from "lucide-react";

function PlayerCard({ player, isSelected, selection, avg, onToggle, onProfileChange, getScoreColor, getPlayerName }) {
  const isSetter = player.position === "Levantador";

  return (
    <div
      className={`p-3 rounded-lg border transition-all ${
        isSelected
          ? "border-primary bg-primary/10"
          : "border-border/50 hover:border-primary/50"
      }`}
    >
      <div
        className="flex items-center gap-3 cursor-pointer"
        onClick={onToggle}
        data-testid={`player-select-${player.id}`}
      >
        <Checkbox checked={isSelected} className="pointer-events-none" />
        {player.photo_url ? (
          <img
            src={player.photo_url}
            alt={getPlayerName(player)}
            className="w-10 h-10 rounded-full object-cover"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <User className="h-5 w-5 text-primary" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">{getPlayerName(player)}</p>
          <div className="flex items-center gap-1 flex-wrap">
            {player.yankee && (
              <Badge variant="secondary" className="text-xs font-mono">{player.yankee}</Badge>
            )}
            {player.position && (
              <Badge variant="outline" className="text-xs">{player.position}</Badge>
            )}
            {player.position2 && (
              <Badge variant="outline" className="text-xs text-muted-foreground">{player.position2}</Badge>
            )}
            <Badge
              className={`text-[10px] px-1.5 py-0 ${
                (player.member_type || "membro") === "membro"
                  ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                  : "bg-amber-500/20 text-amber-400 border-amber-500/30"
              }`}
            >
              {(player.member_type || "membro") === "membro" ? "Membro" : "Convidado"}
            </Badge>
          </div>
        </div>
        <span className={`font-bold ${getScoreColor(parseFloat(avg))}`}>{avg}</span>
      </div>

      {isSetter && isSelected && (
        <div className="mt-2 pt-2 border-t border-border/30" onClick={(e) => e.stopPropagation()}>
          <Select
            value={selection?.profile_type || "levantador"}
            onValueChange={(v) => onProfileChange(player.id, v)}
          >
            <SelectTrigger className="h-8 text-xs bg-input/50">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="levantador">
                <span className="font-medium">Jogar como Levantador</span>
              </SelectItem>
              <SelectItem value="jogador">
                <span className="font-medium">Jogar como {player.position2 || "Jogador"}</span>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}

export default function PlayerSelectionCard({
  isLoading,
  playersByPosition,
  isPlayerSelected,
  getPlayerSelection,
  getPlayerAverage,
  togglePlayer,
  changePlayerProfile,
  getScoreColor,
  getPlayerName,
  namesInput,
  setNamesInput,
  handleSelectByNames,
}) {
  return (
    <Card className="bg-card border-border/50 shadow-xl">
      <CardHeader>
        <CardTitle className="text-xl font-bold tracking-tight flex items-center gap-2">
          <Users className="h-5 w-5 text-primary" />
          Selecionar Jogadores
        </CardTitle>
        <CardDescription>
          Selecione 18 jogadores (mínimo 3 jogando como levantador). Levantadores podem jogar como &quot;Jogador&quot;.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">Carregando...</div>
        ) : (
          <div className="space-y-6">
            {/* Paste names to auto-select */}
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Selecionar por Nome (nome e sobrenome)
              </Label>
              <textarea
                className="w-full min-h-[80px] rounded-md border border-border/60 bg-input/50 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary resize-y"
                placeholder={"Cole aqui os nomes completos, separados por vírgula ou quebra de linha.\nExemplo:\nJoão Silva\nMaria Souza"}
                value={namesInput}
                onChange={(e) => setNamesInput(e.target.value)}
              />
              <button
                type="button"
                className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-3"
                onClick={handleSelectByNames}
              >
                Selecionar jogadores pelos nomes colados
              </button>
            </div>

            {Object.entries(playersByPosition).map(([position, posPlayers]) => (
              <div key={position}>
                <h3 className="text-sm font-bold uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2">
                  {position}
                  {position === "Levantador" && (
                    <span className="text-xs text-primary font-normal normal-case">(2 perfis dispondveis)</span>
                  )}
                  <Badge variant="outline" className="text-xs">
                    {posPlayers.filter((p) => isPlayerSelected(p.id)).length}/{posPlayers.length}
                  </Badge>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {posPlayers.map((player) => (
                    <PlayerCard
                      key={player.id}
                      player={player}
                      isSelected={isPlayerSelected(player.id)}
                      selection={getPlayerSelection(player.id)}
                      avg={getPlayerAverage(player.id)}
                      onToggle={() => togglePlayer(player.id)}
                      onProfileChange={changePlayerProfile}
                      getScoreColor={getScoreColor}
                      getPlayerName={getPlayerName}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
